<template>
  <div class="transaction-detail-page">
    <div class="flex align-items-start justify-content-between mb-4">
      <div class="flex align-items-center">
        <div>
          <h2 class="text-2xl font-semibold m-0">
            {{
              transaction?.transaction_type === 'purchase'
                ? 'Purchase'
                : transaction?.transaction_type === 'sale'
                  ? 'Sale'
                  : 'Transaction'
            }}
            Details
            <span v-if="transaction?.transaction_number" class="text-lg text-500 ml-2">
              #{{ transaction.transaction_number }}
            </span>
          </h2>
          <div class="flex flex-wrap gap-4 mt-2">
            <div class="flex align-items-center gap-2">
              <span class="font-medium">{{ formatDate(transaction?.transaction_date) }}</span>
            </div>
            <div class="flex align-items-center gap-2">
              <span class="text-500">Payment:</span>
              <Tag
                :value="getPaymentStatusLabel(transaction?.payment_status || 'pending')"
                :severity="getPaymentStatusSeverity(transaction?.payment_status || 'pending')"
              />
              <Tag
                :value="getPaymentMethodLabel(transaction?.payment_method || 'cash')"
                severity="info"
              />
            </div>
            <div class="flex align-items-center gap-2">
              <span class="text-500">Total:</span>
              <span class="font-medium text-xl">{{
                formatCurrency(transaction?.total_amount || 0)
              }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex gap-2">
        <Button
          label="Edit"
          icon="pi pi-pencil"
          class="p-button-outlined"
          @click="editTransaction"
        />
        <SplitButton
          label="Finalize & Print"
          icon="pi pi-print"
          :model="printOptions"
          class="p-button-outlined"
          @click="finalizeAndPrint"
        />
      </div>
    </div>

    <div v-if="loading" class="flex flex-column align-items-center py-5">
      <i class="pi pi-spin pi-spinner text-6xl text-primary mb-4"></i>
      <p>Loading transaction details...</p>
    </div>

    <div v-else-if="!transaction" class="flex flex-column align-items-center py-5">
      <i class="pi pi-exclamation-circle text-6xl text-yellow-500 mb-4"></i>
      <p class="text-xl">Transaction not found</p>
      <Button label="Back to Transactions" icon="pi pi-arrow-left" class="mt-3" @click="goBack" />
    </div>

    <div v-else>
      <div class="grid align-items-stretch">
        <div class="col-12">
          <!-- Party Section -->
          <div class="mb-4">
            <div class="flex align-items-center mb-3">
              <i class="pi pi-users mr-2"></i>
              <h5 class="m-0 text-xl">Party</h5>
            </div>
            <div class="flex flex-column gap-4 align-items-start">
              <!-- Person Section -->
              <div
                v-if="hasPerson"
                class="flex w-full align-items-start justify-content-between p-3 surface-100 border-round cursor-pointer hover:surface-200 transition-colors"
                @click="showPersonDrawer = true"
              >
                <div class="flex align-items-center gap-3">
                  <div
                    class="flex align-items-center justify-content-center"
                    style="width: 96px; height: 96px"
                  >
                    <img
                      v-if="personPhotoIdUrl"
                      :src="personPhotoIdUrl"
                      :alt="`${personName} photo ID`"
                      class="border-round"
                      style="width: 100%; height: 100%; object-fit: cover"
                    />
                    <i v-else class="pi pi-user text-2xl text-primary"></i>
                  </div>
                  <div>
                    <div class="text-900 text-xl font-semibold">{{ personName }}</div>
                  </div>
                </div>
                <i class="pi pi-chevron-right text-500"></i>
              </div>

              <!-- Organization Section -->
              <div
                v-if="hasOrganization"
                class="flex w-full align-items-center justify-content-between p-3 surface-100 border-round cursor-pointer hover:surface-200 transition-colors"
                @click="showOrganizationDrawer = true"
              >
                <div class="flex align-items-center gap-3">
                  <i class="pi pi-building text-2xl text-primary"></i>
                  <div>
                    <div class="text-900 font-semibold">{{ organizationName }}</div>
                  </div>
                </div>
                <i class="pi pi-chevron-right text-500"></i>
              </div>

              <!-- No party data -->
              <div
                v-if="!hasPerson && !hasOrganization"
                class="flex align-items-center justify-content-center p-4 text-500"
              >
                <i class="pi pi-info-circle mr-2"></i>
                <span>No party information available</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TransactionItemPhotoList :items="items" :commodity-types="commodityTypes" :readonly="true" />
    </div>

    <!-- Person Drawer -->
    <PersonDrawer
      v-model:visible="showPersonDrawer"
      :person="person"
      position="right"
      size="!w-full md:!w-96 lg:!w-[28rem]"
    />

    <!-- Organization Drawer -->
    <OrganizationDrawer
      v-model:visible="showOrganizationDrawer"
      :organization="transaction?.organizationSnapshot || null"
      position="right"
      size="!w-full md:!w-96 lg:!w-[28rem]"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import type { CommodityType } from '@/types/CommodityType'
import { type TransactionRecord, type TransactionItem } from '@/types/Transaction'
import { useToast } from 'primevue/usetoast'
import { TransactionPartyModel } from '@/models/TransactionPartyModel'
import { useTransactionStore } from '@/stores/transactionStoreOld'
import { useNotificationService } from '@/services/notificationService'
import { usePersonStore } from '@/stores/v2/person'
import { useCommodityTypeService } from '@/services/commodityTypeService'
import { useTransactionService } from '@/services/transactionService'

// PrimeVue components
import Card from 'primevue/card'
import Button from 'primevue/button'
import SplitButton from 'primevue/splitbutton'
import Tag from 'primevue/tag'

// Import the TransactionItemPhotoList component
import TransactionItemPhotoList from '@/components/TransactionItemPhotoList.vue'
import PersonDrawer from '@/components/person/PersonDrawer.vue'
import OrganizationDrawer from '@/components/organization/OrganizationDrawer.vue'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const transactionStore = useTransactionStore()
const notificationService = useNotificationService()
const personStore = usePersonStore()
const commodityTypeService = useCommodityTypeService()
const transactionService = useTransactionService()
// State
const transaction = ref<TransactionRecord | null>(null)
const items = ref<TransactionItem[]>([])
const loading = ref(true)
const commodityTypes = ref<CommodityType[]>([])
const isProcessing = ref(false)
const person = ref<any>(null)
const showPersonDrawer = ref(false)
const showOrganizationDrawer = ref(false)

// Development mode check
const isDev = computed(() => import.meta.env.DEV)

// Computed property for transaction party
const transactionParty = computed(() => {
  if (!transaction.value) return new TransactionPartyModel({} as TransactionRecord)
  return new TransactionPartyModel(transaction.value)
})

// Total weight of all items
const totalWeight = computed(() => {
  return items.value.reduce((sum, item) => sum + (item.weight?.net || 0), 0)
})

// Party computed values with snapshot-first fallbacks
const hasPerson = computed(() => {
  return !!(
    transaction.value?.personSnapshot?.firstName ||
    transaction.value?.personSnapshot?.lastName ||
    transaction.value?.party_data?.name
  )
})

const personName = computed(() => {
  const ps = transaction.value?.personSnapshot
  if (ps?.firstName || ps?.lastName) {
    return [ps?.firstName, ps?.middleName, ps?.lastName].filter(Boolean).join(' ')
  }
  return transaction.value?.party_data?.name || 'Unknown'
})

const personEmail = computed(() => {
  return transaction.value?.personSnapshot?.email || transaction.value?.party_data?.email || null
})

const personPhotoIdUrl = computed(() => {
  return transaction.value?.personSnapshot?.photoIdUrl || null
})

const personPhone = computed(() => {
  return transaction.value?.personSnapshot?.phone || transaction.value?.party_data?.phone || null
})

const personAddress = computed(() => {
  const ps = transaction.value?.personSnapshot
  const parts = [ps?.streetAddress, ps?.city, ps?.state, ps?.postalCode, ps?.country]
  const snapshotAddress = parts.filter(Boolean).join(', ')
  return snapshotAddress || transaction.value?.party_data?.address || null
})

const idNumber = computed(() => {
  return transaction.value?.personSnapshot?.idNumber || 'N/A'
})

const hasOrganization = computed(() => {
  return !!(
    transaction.value?.organizationSnapshot?.name || transaction.value?.party_data?.company_name
  )
})

const organizationName = computed(() => {
  return (
    transaction.value?.organizationSnapshot?.name ||
    transaction.value?.party_data?.company_name ||
    'Unknown'
  )
})

const organizationEmail = computed(() => {
  return transaction.value?.organizationSnapshot?.email || null
})

const organizationPhone = computed(() => {
  return transaction.value?.organizationSnapshot?.phone || null
})

const organizationAddress = computed(() => {
  const os = transaction.value?.organizationSnapshot
  const parts = [os?.streetAddress, os?.city, os?.state, os?.postalCode, os?.country]
  return parts.filter(Boolean).join(', ')
})

const printOptions = ref([
  {
    label: 'Print Only',
    icon: 'pi pi-print',
    command: () => goToPrintView(),
  },
])

// Payment methods
const paymentMethods = [
  { label: 'Cash', value: 'cash' },
  { label: 'Check', value: 'check' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
  { label: 'Credit', value: 'credit' },
  { label: 'Debit', value: 'debit' },
]

// Payment statuses
const paymentStatuses = [
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
]

// Methods
function goBack() {
  router.push('/transaction')
}

function editTransaction() {
  router.push(`/transaction/${route.params.id}/edit`)
}

function goToPrintView() {
  router.push(`/transaction/${route.params.id}/print`)
}

// Update the finalizeTransaction function to use the store
async function finalizeTransaction() {
  if (!transaction.value?.id) return

  try {
    await transactionStore.finalizeTransaction(transaction.value.id)
    return Promise.resolve()
  } catch (error) {
    return Promise.reject(error)
  }
}

// Update the finalizeAndPrint function to handle the rejection properly
async function finalizeAndPrint() {
  if (!transaction.value) return

  // Check if already completed
  if (transaction.value.payment_status === 'completed') {
    toast.add({
      severity: 'info',
      summary: 'Already Finalized',
      detail: 'This transaction is already finalized. Proceeding to print.',
      life: 3000,
    })
    goToPrintView()
    return
  }

  isProcessing.value = true

  try {
    await finalizeTransaction()
    // Fire notification (non-blocking)
    notificationService
      .notifyTransactionFinalized({
        transactionId: transaction.value.id!,
        transactionNumber: transaction.value.transaction_number as any,
        totalAmount: transaction.value.total_amount as any,
        partyName: transactionParty.value.name,
        partyEmail: transactionParty.value.email || undefined,
        transactionType: transaction.value.transaction_type,
        personName: transaction.value.personSnapshot?.firstName
          ? `${transaction.value.personSnapshot?.firstName} ${transaction.value.personSnapshot?.lastName || ''}`.trim()
          : undefined,
        organizationName: transaction.value.organizationSnapshot?.name,
        items: items.value,
      })
      .catch((err) => console.warn('Notify transaction finalized failed', err))
    toast.add({
      severity: 'success',
      summary: 'Transaction Finalized',
      detail: 'Transaction has been successfully finalized.',
      life: 3000,
    })
    // Proceed to print
    goToPrintView()
  } catch (error) {
    console.error('Error finalizing transaction:', error)
    // Don't show a generic error toast here since we already show a specific one in finalizeTransaction
    if (error instanceof Error && error.message !== 'Missing images') {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to finalize transaction. Please try again.',
        life: 5000,
      })
    }
  } finally {
    isProcessing.value = false
  }
}

function formatDate(timestamp: Date | { toDate: () => Date } | undefined): string {
  if (!timestamp) return 'N/A'
  const date = 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value)
}

function getPaymentMethodLabel(method: string): string {
  const methodObj = paymentMethods.find((m) => m.value === method)
  return methodObj ? methodObj.label : method
}

function getPaymentStatusLabel(status: string): string {
  const statusObj = paymentStatuses.find((s) => s.value === status)
  return statusObj ? statusObj.label : status
}

function getPaymentStatusSeverity(status: string): string {
  switch (status) {
    case 'completed':
      return 'success'
    case 'pending':
      return 'warning'
    case 'cancelled':
      return 'danger'
    default:
      return 'info'
  }
}

// Fetch transaction data
async function fetchTransaction() {
  loading.value = true
  try {
    // Fetch commodity types using service
    commodityTypes.value = await commodityTypeService.getActiveCommodityTypes()

    const transactionId = route.params.id as string
    const { transaction: transactionData, draft } =
      await transactionService.getTransactionWithDraft(transactionId)

    if (!transactionData) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Transaction not found',
        life: 5000,
      })
      transaction.value = null
      return
    }

    // Process items
    if (transactionData.items && Array.isArray(transactionData.items)) {
      items.value = await Promise.all(
        transactionData.items.map(async (item) => {
          let commodityName = 'Unknown'
          const rawId: any = item.commodity_type_id as any
          const commodityTypeId: string =
            typeof rawId === 'object' && rawId && 'id' in rawId
              ? (rawId.id as string)
              : (rawId as string)

          // Find the commodity name from our loaded commodity types
          const commodity = commodityTypes.value.find((c) => c.id === commodityTypeId)
          if (commodity) {
            commodityName = commodity.name
          }

          return {
            ...item,
            commodity_type_id: commodityTypeId,
            commodity_name: commodityName,
          } as TransactionItem
        }),
      )
    }

    transaction.value = {
      id: transactionId,
      ...transactionData,
    }

    // Fetch person data if personId exists and is valid
    if (transactionData.personId && transactionData.personId.trim() !== '') {
      try {
        const personData = await personStore.get(transactionData.personId)
        if (personData) {
          person.value = personData
          console.log('Fetched person data:', personData)
          console.log('Person attachments:', personData.attachments)
        }
      } catch (error) {
        console.error('Error fetching person:', error)
        // Don't show error toast for missing person data as it's not critical
        console.warn('Person data not found for transaction:', transactionData.personId)
      }
    }
  } catch (error) {
    console.error('Error fetching transaction:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load transaction details',
      life: 5000,
    })
  } finally {
    loading.value = false
  }
}

// Helper functions
function getFileIcon(fileType: string) {
  if (fileType.startsWith('image/')) return 'pi pi-image text-blue-500'
  if (fileType === 'application/pdf') return 'pi pi-file-pdf text-red-500'
  return 'pi pi-file text-gray-500'
}

// Load data when component mounts
onMounted(() => {
  fetchTransaction()
})
</script>

<style scoped>
.transaction-detail-page {
  padding-top: 1.5rem;
  padding-bottom: 3rem;
  max-width: 1200px;
  margin: 0 auto;
}

/* Custom styling for cards */
:deep(.p-card) {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.3s ease;
}

:deep(.p-card:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:deep(.p-card .p-card-header) {
  padding: 1rem 1.5rem;
}

:deep(.p-card .p-card-content) {
  padding: 1.5rem;
}

:deep(.p-splitbutton) {
  display: inline-flex;
}

:deep(.p-splitbutton .p-splitbutton-menubutton) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.processing-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Mobile responsiveness */
@media screen and (max-width: 768px) {
  .text-xl {
    font-size: 1.25rem !important;
  }

  /* Stack layout improvements */
  :deep(.p-datatable-tbody > tr > td .p-column-title) {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
}
</style>
